/**
 *
 */

import { __extends, __awaiter, __generator, __spreadArray } from 'tslib';

/**
 * Enum for the source state.
 *
 * @readonly
 * @enum {number}
 */
var State;
(function (State) {
    State[State["Success"] = 0] = "Success";
    State[State["Undefined"] = -1] = "Undefined";
    State[State["NotFunction"] = -2] = "NotFunction";
    State[State["UnexpectedBehaviour"] = -3] = "UnexpectedBehaviour";
    State[State["Null"] = -4] = "Null";
})(State || (State = {}));
/**
 * Enum for types of bots.
 * Specific types of bots come first, followed by automation technologies.
 *
 * @readonly
 * @enum {string}
 */
var BotKind;
(function (BotKind) {
    BotKind["Awesomium"] = "awesomium";
    BotKind["Cef"] = "cef";
    BotKind["CefSharp"] = "cefsharp";
    BotKind["CoachJS"] = "coachjs";
    BotKind["Electron"] = "electron";
    BotKind["FMiner"] = "fminer";
    BotKind["Geb"] = "geb";
    BotKind["NightmareJS"] = "nightmarejs";
    BotKind["Phantomas"] = "phantomas";
    BotKind["PhantomJS"] = "phantomjs";
    BotKind["Rhino"] = "rhino";
    BotKind["Selenium"] = "selenium";
    BotKind["Sequentum"] = "sequentum";
    BotKind["SlimerJS"] = "slimerjs";
    BotKind["WebDriverIO"] = "webdriverio";
    BotKind["WebDriver"] = "webdriver";
    BotKind["HeadlessChrome"] = "headless_chrome";
    BotKind["Unknown"] = "unknown";
})(BotKind || (BotKind = {}));
/**
 * Bot detection error.
 */
var BotdError = /** @class */ (function (_super) {
    __extends(BotdError, _super);
    /**
     * Creates a new BotdError.
     *
     * @class
     */
    function BotdError(state, message) {
        var _this = _super.call(this, message) || this;
        _this.state = state;
        _this.name = 'BotdError';
        Object.setPrototypeOf(_this, BotdError.prototype);
        return _this;
    }
    return BotdError;
}(Error));
var BrowserEngineKind;
(function (BrowserEngineKind) {
    BrowserEngineKind["Unknown"] = "unknown";
    BrowserEngineKind["Chromium"] = "chromium";
    BrowserEngineKind["Gecko"] = "gecko";
    BrowserEngineKind["Webkit"] = "webkit";
})(BrowserEngineKind || (BrowserEngineKind = {}));
var BrowserKind;
(function (BrowserKind) {
    BrowserKind["Unknown"] = "unknown";
    BrowserKind["Chrome"] = "chrome";
    BrowserKind["Firefox"] = "firefox";
    BrowserKind["Opera"] = "opera";
    BrowserKind["Safari"] = "safari";
    BrowserKind["IE"] = "internet_explorer";
    BrowserKind["WeChat"] = "wechat";
})(BrowserKind || (BrowserKind = {}));

function detectAppVersion(_a) {
    var appVersion = _a.appVersion;
    if (appVersion.state !== State.Success)
        return false;
    if (/headless/i.test(appVersion.value))
        return BotKind.HeadlessChrome;
    if (/electron/i.test(appVersion.value))
        return BotKind.Electron;
    if (/slimerjs/i.test(appVersion.value))
        return BotKind.SlimerJS;
}

function arrayIncludes(arr, value) {
    return arr.indexOf(value) !== -1;
}
function strIncludes(str, value) {
    return str.indexOf(value) !== -1;
}
function arrayFind(array, callback) {
    if ('find' in array)
        return array.find(callback);
    for (var i = 0; i < array.length; i++) {
        if (callback(array[i], i, array))
            return array[i];
    }
    return undefined;
}

function getObjectProps(obj) {
    return Object.getOwnPropertyNames(obj);
}
function includes(arr) {
    var keys = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        keys[_i - 1] = arguments[_i];
    }
    var _loop_1 = function (key) {
        if (typeof key === 'string') {
            if (arrayIncludes(arr, key))
                return { value: true };
        }
        else {
            var match = arrayFind(arr, function (value) { return key.test(value); });
            if (match != null)
                return { value: true };
        }
    };
    for (var _a = 0, keys_1 = keys; _a < keys_1.length; _a++) {
        var key = keys_1[_a];
        var state_1 = _loop_1(key);
        if (typeof state_1 === "object")
            return state_1.value;
    }
    return false;
}
function countTruthy(values) {
    return values.reduce(function (sum, value) { return sum + (value ? 1 : 0); }, 0);
}

function detectDocumentAttributes(_a) {
    var documentElementKeys = _a.documentElementKeys;
    if (documentElementKeys.state !== State.Success)
        return false;
    if (includes(documentElementKeys.value, 'selenium', 'webdriver', 'driver')) {
        return BotKind.Selenium;
    }
}

function detectErrorTrace(_a) {
    var errorTrace = _a.errorTrace;
    if (errorTrace.state !== State.Success)
        return false;
    if (/PhantomJS/i.test(errorTrace.value))
        return BotKind.PhantomJS;
}

function getBrowserEngineKind() {
    var _a, _b;
    // Based on research in October 2020. Tested to detect Chromium 42-86.
    var w = window;
    var n = navigator;
    if (countTruthy([
        'webkitPersistentStorage' in n,
        'webkitTemporaryStorage' in n,
        n.vendor.indexOf('Google') === 0,
        'webkitResolveLocalFileSystemURL' in w,
        'BatteryManager' in w,
        'webkitMediaStream' in w,
        'webkitSpeechGrammar' in w,
    ]) >= 5) {
        return BrowserEngineKind.Chromium;
    }
    if (countTruthy([
        'ApplePayError' in w,
        'CSSPrimitiveValue' in w,
        'Counter' in w,
        n.vendor.indexOf('Apple') === 0,
        'getStorageUpdates' in n,
        'WebKitMediaKeys' in w,
    ]) >= 4) {
        return BrowserEngineKind.Webkit;
    }
    if (countTruthy([
        'buildID' in navigator,
        'MozAppearance' in ((_b = (_a = document.documentElement) === null || _a === void 0 ? void 0 : _a.style) !== null && _b !== void 0 ? _b : {}),
        'onmozfullscreenchange' in w,
        'mozInnerScreenX' in w,
        'CSSMozDocumentRule' in w,
        'CanvasCaptureMediaStream' in w,
    ]) >= 4) {
        return BrowserEngineKind.Gecko;
    }
    return BrowserEngineKind.Unknown;
}
function getBrowserKind() {
    var _a;
    var userAgent = (_a = navigator.userAgent) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    if (strIncludes(userAgent, 'wechat')) {
        return BrowserKind.WeChat;
    }
    else if (strIncludes(userAgent, 'firefox')) {
        return BrowserKind.Firefox;
    }
    else if (strIncludes(userAgent, 'opera') || strIncludes(userAgent, 'opr')) {
        return BrowserKind.Opera;
    }
    else if (strIncludes(userAgent, 'chrome')) {
        return BrowserKind.Chrome;
    }
    else if (strIncludes(userAgent, 'safari')) {
        return BrowserKind.Safari;
    }
    else if (strIncludes(userAgent, 'trident') || strIncludes(userAgent, 'msie')) {
        return BrowserKind.IE;
    }
    else {
        return BrowserKind.Unknown;
    }
}
// Source: https://github.com/fingerprintjs/fingerprintjs/blob/master/src/utils/browser.ts#L223
function isAndroid() {
    var browserEngineKind = getBrowserEngineKind();
    var isItChromium = browserEngineKind === BrowserEngineKind.Chromium;
    var isItGecko = browserEngineKind === BrowserEngineKind.Gecko;
    // Only 2 browser engines are presented on Android.
    // Actually, there is also Android 4.1 browser, but it's not worth detecting it at the moment.
    if (!isItChromium && !isItGecko)
        return false;
    var w = window;
    // Chrome removes all words "Android" from `navigator` when desktop version is requested
    // Firefox keeps "Android" in `navigator.appVersion` when desktop version is requested
    return (countTruthy([
        'onorientationchange' in w,
        'orientation' in w,
        isItChromium && !('SharedWorker' in w),
        isItGecko && /android/i.test(navigator.appVersion),
    ]) >= 2);
}
function isDesktopSafari() {
    if (getBrowserEngineKind() !== BrowserEngineKind.Webkit) {
        return false;
    }
    var w = window;
    return (countTruthy([
        'safari' in w,
        !('DeviceMotionEvent' in w),
        !('ongestureend' in w),
        !('standalone' in navigator),
    ]) >= 3);
}
function getDocumentFocus() {
    if (document.hasFocus === undefined) {
        return false;
    }
    return document.hasFocus();
}
function isChromium86OrNewer() {
    // Checked in Chrome 85 vs Chrome 86 both on desktop and Android
    var w = window;
    return (countTruthy([
        !('MediaSettingsRange' in w),
        'RTCEncodedAudioFrame' in w,
        '' + w.Intl === '[object Intl]',
        '' + w.Reflect === '[object Reflect]',
    ]) >= 3);
}

function detectEvalLengthInconsistency(_a) {
    var evalLength = _a.evalLength;
    if (evalLength.state !== State.Success)
        return;
    var length = evalLength.value;
    var browser = getBrowserKind();
    var browserEngine = getBrowserEngineKind();
    return ((length === 37 && !arrayIncludes([BrowserEngineKind.Webkit, BrowserEngineKind.Gecko], browserEngine)) ||
        (length === 39 && !arrayIncludes([BrowserKind.IE], browser)) ||
        (length === 33 && !arrayIncludes([BrowserEngineKind.Chromium], browserEngine)));
}

function detectFunctionBind(_a) {
    var functionBind = _a.functionBind;
    if (functionBind.state === State.NotFunction)
        return BotKind.PhantomJS;
}

function detectLanguagesLengthInconsistency(_a) {
    var languages = _a.languages;
    if (languages.state === State.Success && languages.value.length === 0) {
        return BotKind.HeadlessChrome;
    }
}

function detectMimeTypesConsistent(_a) {
    var mimeTypesConsistent = _a.mimeTypesConsistent;
    if (mimeTypesConsistent.state === State.Success && !mimeTypesConsistent.value) {
        return BotKind.Unknown;
    }
}

function detectNotificationPermissions(_a) {
    var notificationPermissions = _a.notificationPermissions;
    var browserKind = getBrowserKind();
    if (browserKind !== BrowserKind.Chrome)
        return false;
    if (notificationPermissions.state === State.Success && notificationPermissions.value) {
        return BotKind.HeadlessChrome;
    }
}

function detectPluginsArray(_a) {
    var pluginsArray = _a.pluginsArray;
    if (pluginsArray.state === State.Success && !pluginsArray.value)
        return BotKind.HeadlessChrome;
}

function detectPluginsLengthInconsistency(_a) {
    var pluginsLength = _a.pluginsLength;
    if (pluginsLength.state !== State.Success)
        return;
    var browserEngineKind = getBrowserEngineKind();
    // Chromium based android browsers and mobile webkit based browsers have 0 plugins length.
    if ((browserEngineKind === BrowserEngineKind.Chromium && isAndroid()) ||
        (browserEngineKind === BrowserEngineKind.Webkit && !isDesktopSafari()))
        return;
    if (pluginsLength.value === 0)
        return BotKind.HeadlessChrome;
}

function detectProcess(_a) {
    var _b;
    var process = _a.process;
    if (process.state !== State.Success)
        return false;
    if (process.value.type === 'renderer' || ((_b = process.value.versions) === null || _b === void 0 ? void 0 : _b.electron) != null)
        return BotKind.Electron;
}

function detectProductSub(_a) {
    var productSub = _a.productSub;
    if (productSub.state !== State.Success)
        return false;
    var browserKind = getBrowserKind();
    if ((browserKind === BrowserKind.Chrome ||
        browserKind === BrowserKind.Safari ||
        browserKind === BrowserKind.Opera ||
        browserKind === BrowserKind.WeChat) &&
        productSub.value !== '20030107')
        return BotKind.Unknown;
}

function detectUserAgent(_a) {
    var userAgent = _a.userAgent;
    if (userAgent.state !== State.Success)
        return false;
    if (/PhantomJS/i.test(userAgent.value))
        return BotKind.PhantomJS;
    if (/Headless/i.test(userAgent.value))
        return BotKind.HeadlessChrome;
    if (/Electron/i.test(userAgent.value))
        return BotKind.Electron;
    if (/slimerjs/i.test(userAgent.value))
        return BotKind.SlimerJS;
}

function detectWebDriver(_a) {
    var webDriver = _a.webDriver;
    if (webDriver.state === State.Success && webDriver.value)
        return BotKind.HeadlessChrome;
}

function detectWebGL(_a) {
    var webGL = _a.webGL;
    if (webGL.state === State.Success) {
        var _b = webGL.value, vendor = _b.vendor, renderer = _b.renderer;
        if (vendor == 'Brian Paul' && renderer == 'Mesa OffScreen') {
            return BotKind.HeadlessChrome;
        }
    }
}

function detectWindowExternal(_a) {
    var windowExternal = _a.windowExternal;
    if (windowExternal.state !== State.Success)
        return false;
    if (/Sequentum/i.test(windowExternal.value))
        return BotKind.Sequentum;
}

function detectWindowSize(_a) {
    var windowSize = _a.windowSize;
    if (windowSize.state !== State.Success)
        return false;
    var _b = windowSize.value, outerWidth = _b.outerWidth, outerHeight = _b.outerHeight;
    // When a page is opened in a new tab without focusing it right away, the window outer size is 0x0
    if (!getDocumentFocus())
        return;
    if (outerWidth === 0 && outerHeight === 0)
        return BotKind.HeadlessChrome;
}

function detectDistinctiveProperties(_a) {
    var distinctiveProps = _a.distinctiveProps;
    if (distinctiveProps.state !== State.Success)
        return false;
    var value = distinctiveProps.value;
    var bot;
    for (bot in value)
        if (value[bot])
            return bot;
}

function detectBatteryLevel(_a) {
    var batteryInstance = _a.batteryInstance;
    if (batteryInstance.state !== State.Success)
        return false;
    if (!(batteryInstance.value.batterInstanceResult instanceof window.BatteryManager))
        return BotKind.Unknown;
}

function detectLocalSorage(_a) {
    var isLocalStorage = _a.isLocalStorage;
    if (isLocalStorage.state !== State.Success)
        return false;
    if (typeof isLocalStorage.value !== 'object')
        return BotKind.Unknown;
}

// import { detectSessionSorage } from './hs_ss'
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
var detectors = {
    detectAppVersion: detectAppVersion,
    detectDocumentAttributes: detectDocumentAttributes,
    detectErrorTrace: detectErrorTrace,
    detectEvalLengthInconsistency: detectEvalLengthInconsistency,
    detectFunctionBind: detectFunctionBind,
    detectLanguagesLengthInconsistency: detectLanguagesLengthInconsistency,
    detectNotificationPermissions: detectNotificationPermissions,
    detectPluginsArray: detectPluginsArray,
    detectPluginsLengthInconsistency: detectPluginsLengthInconsistency,
    detectProcess: detectProcess,
    detectUserAgent: detectUserAgent,
    detectWebDriver: detectWebDriver,
    detectWebGL: detectWebGL,
    detectWindowExternal: detectWindowExternal,
    detectWindowSize: detectWindowSize,
    detectMimeTypesConsistent: detectMimeTypesConsistent,
    detectProductSub: detectProductSub,
    detectDistinctiveProperties: detectDistinctiveProperties,
    detectBatteryLevel: detectBatteryLevel,
    detectLocalSorage: detectLocalSorage,
    // detectSessionSorage,
};

function getAppVersion() {
    var appVersion = navigator.appVersion;
    if (appVersion == undefined) {
        throw new BotdError(State.Undefined, 'navigator.appVersion is undefined');
    }
    return appVersion;
}

function getDocumentElementKeys() {
    if (document.documentElement === undefined) {
        throw new BotdError(State.Undefined, 'document.documentElement is undefined');
    }
    var documentElement = document.documentElement;
    if (typeof documentElement.getAttributeNames !== 'function') {
        throw new BotdError(State.NotFunction, 'document.documentElement.getAttributeNames is not a function');
    }
    return documentElement.getAttributeNames();
}

function getErrorTrace() {
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        null[0]();
    }
    catch (error) {
        if (error instanceof Error && error['stack'] != null) {
            return error.stack.toString();
        }
    }
    throw new BotdError(State.UnexpectedBehaviour, 'errorTrace signal unexpected behaviour');
}

function getEvalLength() {
    return eval.toString().length;
}

function getFunctionBind() {
    if (Function.prototype.bind === undefined) {
        throw new BotdError(State.NotFunction, 'Function.prototype.bind is undefined');
    }
    return Function.prototype.bind.toString();
}

function getLanguages() {
    var n = navigator;
    var result = [];
    var language = n.language || n.userLanguage || n.browserLanguage || n.systemLanguage;
    if (language !== undefined) {
        result.push([language]);
    }
    if (Array.isArray(n.languages)) {
        var browserEngine = getBrowserEngineKind();
        // Starting from Chromium 86, there is only a single value in `navigator.language` in Incognito mode:
        // the value of `navigator.language`. Therefore, the value is ignored in this browser.
        if (!(browserEngine === BrowserEngineKind.Chromium && isChromium86OrNewer())) {
            result.push(n.languages);
        }
    }
    else if (typeof n.languages === 'string') {
        var languages = n.languages;
        if (languages) {
            result.push(languages.split(','));
        }
    }
    return result;
}

function areMimeTypesConsistent() {
    if (navigator.mimeTypes === undefined) {
        throw new BotdError(State.Undefined, 'navigator.mimeTypes is undefined');
    }
    var mimeTypes = navigator.mimeTypes;
    var isConsistent = Object.getPrototypeOf(mimeTypes) === MimeTypeArray.prototype;
    for (var i = 0; i < mimeTypes.length; i++) {
        isConsistent && (isConsistent = Object.getPrototypeOf(mimeTypes[i]) === MimeType.prototype);
    }
    return isConsistent;
}

function getNotificationPermissions() {
    return __awaiter(this, void 0, void 0, function () {
        var permissions, permissionStatus;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (window.Notification === undefined) {
                        throw new BotdError(State.Undefined, 'window.Notification is undefined');
                    }
                    if (navigator.permissions === undefined) {
                        throw new BotdError(State.Undefined, 'navigator.permissions is undefined');
                    }
                    permissions = navigator.permissions;
                    if (typeof permissions.query !== 'function') {
                        throw new BotdError(State.NotFunction, 'navigator.permissions.query is not a function');
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, permissions.query({ name: 'notifications' })];
                case 2:
                    permissionStatus = _a.sent();
                    return [2 /*return*/, window.Notification.permission === 'denied' && permissionStatus.state === 'prompt'];
                case 3:
                    _a.sent();
                    throw new BotdError(State.UnexpectedBehaviour, 'notificationPermissions signal unexpected behaviour');
                case 4: return [2 /*return*/];
            }
        });
    });
}

function getPluginsArray() {
    if (navigator.plugins === undefined) {
        throw new BotdError(State.Undefined, 'navigator.plugins is undefined');
    }
    if (window.PluginArray === undefined) {
        throw new BotdError(State.Undefined, 'window.PluginArray is undefined');
    }
    return navigator.plugins instanceof PluginArray;
}

function getPluginsLength() {
    if (navigator.plugins === undefined) {
        throw new BotdError(State.Undefined, 'navigator.plugins is undefined');
    }
    if (navigator.plugins.length === undefined) {
        throw new BotdError(State.UnexpectedBehaviour, 'navigator.plugins.length is undefined');
    }
    return navigator.plugins.length;
}

function getProcess() {
    if (window.process === undefined) {
        throw new BotdError(State.Undefined, 'window.process is undefined');
    }
    return window.process;
}

function getProductSub() {
    var productSub = navigator.productSub;
    if (productSub === undefined) {
        throw new BotdError(State.Undefined, 'navigator.productSub is undefined');
    }
    return productSub;
}

function getRTT() {
    if (navigator.connection === undefined) {
        throw new BotdError(State.Undefined, 'navigator.connection is undefined');
    }
    if (navigator.connection.rtt === undefined) {
        throw new BotdError(State.Undefined, 'navigator.connection.rtt is undefined');
    }
    return navigator.connection.rtt;
}

function getUserAgent() {
    return navigator.userAgent;
}

function getWebDriver() {
    if (navigator.webdriver == undefined) {
        throw new BotdError(State.Undefined, 'navigator.webdriver is undefined');
    }
    return navigator.webdriver;
}

function getWebGL() {
    var canvasElement = document.createElement('canvas');
    if (typeof canvasElement.getContext !== 'function') {
        throw new BotdError(State.NotFunction, 'HTMLCanvasElement.getContext is not a function');
    }
    var webGLContext = canvasElement.getContext('webgl');
    if (webGLContext === null) {
        throw new BotdError(State.Null, 'WebGLRenderingContext is null');
    }
    if (typeof webGLContext.getParameter !== 'function') {
        throw new BotdError(State.NotFunction, 'WebGLRenderingContext.getParameter is not a function');
    }
    var vendor = webGLContext.getParameter(webGLContext.VENDOR);
    var renderer = webGLContext.getParameter(webGLContext.RENDERER);
    return { vendor: vendor, renderer: renderer };
}

function getWindowExternal() {
    if (window.external === undefined) {
        throw new BotdError(State.Undefined, 'window.external is undefined');
    }
    var external = window.external;
    if (typeof external.toString !== 'function') {
        throw new BotdError(State.NotFunction, 'window.external.toString is not a function');
    }
    return external.toString();
}

function getWindowSize() {
    return {
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
    };
}

function checkDistinctiveProperties() {
    var _a;
    // The order in the following list matters, because specific types of bots come first, followed by automation technologies.
    var distinctivePropsList = (_a = {},
        _a[BotKind.Awesomium] = {
            window: ['awesomium'],
        },
        _a[BotKind.Cef] = {
            window: ['RunPerfTest'],
        },
        _a[BotKind.CefSharp] = {
            window: ['CefSharp'],
        },
        _a[BotKind.CoachJS] = {
            window: ['emit'],
        },
        _a[BotKind.FMiner] = {
            window: ['fmget_targets'],
        },
        _a[BotKind.Geb] = {
            window: ['geb'],
        },
        _a[BotKind.NightmareJS] = {
            window: ['__nightmare', 'nightmare'],
        },
        _a[BotKind.Phantomas] = {
            window: ['__phantomas'],
        },
        _a[BotKind.PhantomJS] = {
            window: ['callPhantom', '_phantom'],
        },
        _a[BotKind.Rhino] = {
            window: ['spawn'],
        },
        _a[BotKind.Selenium] = {
            window: ['_Selenium_IDE_Recorder', '_selenium', 'calledSelenium', /^([a-z]){3}_.*_(Array|Promise|Symbol)$/],
            document: ['__selenium_evaluate', 'selenium-evaluate', '__selenium_unwrapped'],
        },
        _a[BotKind.WebDriverIO] = {
            window: ['wdioElectron'],
        },
        _a[BotKind.WebDriver] = {
            window: [
                'webdriver',
                '__webdriverFunc',
                '__lastWatirAlert',
                '__lastWatirConfirm',
                '__lastWatirPrompt',
                '_WEBDRIVER_ELEM_CACHE',
                'ChromeDriverw',
            ],
            document: [
                '__webdriver_script_fn',
                '__driver_evaluate',
                '__webdriver_evaluate',
                '__fxdriver_evaluate',
                '__driver_unwrapped',
                '__webdriver_unwrapped',
                '__fxdriver_unwrapped',
                '__webdriver_script_fn',
                '__webdriver_script_func',
                '__webdriver_script_function',
                '$cdc_asdjflasutopfhvcZLmcf',
                '$cdc_asdjflasutopfhvcZLmcfl_',
                '$chrome_asyncScriptInfo',
                '__$webdriverAsyncExecutor',
            ],
        },
        _a[BotKind.HeadlessChrome] = {
            window: ['domAutomation', 'domAutomationController'],
        },
        _a);
    var botName;
    var result = {};
    var windowProps = getObjectProps(window);
    var documentProps = [];
    if (window.document !== undefined)
        documentProps = getObjectProps(window.document);
    for (botName in distinctivePropsList) {
        var props = distinctivePropsList[botName];
        if (props !== undefined) {
            var windowContains = props.window === undefined ? false : includes.apply(void 0, __spreadArray([windowProps], props.window, false));
            var documentContains = props.document === undefined || !documentProps.length ? false : includes.apply(void 0, __spreadArray([documentProps], props.document, false));
            result[botName] = windowContains || documentContains;
        }
    }
    return result;
}

var getBatteryLevel = function () { return __awaiter(void 0, void 0, void 0, function () {
    var batteryPromise, batterInstanceResult;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                batteryPromise = window.navigator.getBattery();
                return [4 /*yield*/, batteryPromise.then(function (battery) { return battery; })];
            case 1:
                batterInstanceResult = _a.sent();
                return [2 /*return*/, { batterInstanceResult: batterInstanceResult }];
        }
    });
}); };

var checkLocalStorage = function () {
    var localStorage = window.localStorage;
    return localStorage ? true : false;
};

var getabsmall = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('ab_test_vwo_id') || false;
};

var isAddress = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('isAddressOnCart') || false;
};

var isLocationPermission = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('isLocationPermissionGiven') || false;
};

var getPaymentUrl = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('paymentUrl') || false;
};

var previousScreen = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('previousScreen') || false;
};

var serviceWorkerVersion = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('serviceWorkerUpdated') || false;
};

var getSpeechRecognition = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('SPEECH_RECOGNITION_ENABLED') || false;
};

var getStoreId = function () {
    var localStorage = window.localStorage;
    return localStorage.getItem('storeId') || false;
};

function timezone() {
    var dtDate;
    var numOffset;
    var numGMTHours;
    var numOut;
    var strOnError = 'Error';
    dtDate = null;
    numOffset = null;
    numGMTHours = null;
    numOut = null;
    try {
        dtDate = new Date();
        numOffset = dtDate.getTimezoneOffset();
        numGMTHours = (numOffset / 60) * -1;
        numOut = numGMTHours;
        return numOut;
    }
    catch (err) {
        return strOnError;
    }
}

function touchEventFp() {
    var bolTouchEnabled;
    var bolOut;
    bolTouchEnabled = false;
    bolOut = null;
    try {
        if (document.createEvent('TouchEvent')) {
            bolTouchEnabled = true;
        }
        bolOut = bolTouchEnabled;
        return bolOut;
    }
    catch (ignore) {
        bolOut = bolTouchEnabled;
        return bolOut;
    }
}

var sources = {
    userAgent: getUserAgent,
    appVersion: getAppVersion,
    rtt: getRTT,
    windowSize: getWindowSize,
    pluginsLength: getPluginsLength,
    pluginsArray: getPluginsArray,
    errorTrace: getErrorTrace,
    productSub: getProductSub,
    windowExternal: getWindowExternal,
    mimeTypesConsistent: areMimeTypesConsistent,
    evalLength: getEvalLength,
    webGL: getWebGL,
    webDriver: getWebDriver,
    languages: getLanguages,
    notificationPermissions: getNotificationPermissions,
    documentElementKeys: getDocumentElementKeys,
    functionBind: getFunctionBind,
    process: getProcess,
    distinctiveProps: checkDistinctiveProperties,
    batteryInstance: getBatteryLevel,
    isLocalStorage: checkLocalStorage,
    getabsmall: getabsmall,
    getSpeechRecognition: getSpeechRecognition,
    paymentUrl: getPaymentUrl,
    previousScreen: previousScreen,
    isLocationPermissionGiven: isLocationPermission,
    serviceWorkerUpdated: serviceWorkerVersion,
    storeId: getStoreId,
    isAddressOnCart: isAddress,
    // isSessionStorage: checkSessionStorage,
    timezone: timezone,
    touchEventFp: touchEventFp,
};

/* eslint-disable no-prototype-builtins */
(function (name, context, definition) {
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = definition();
  } else {
    context[name] = definition();
  }
})('Fingerprint', window, function () {
  var Fingerprint = function (options) {
    var nativeForEach, nativeMap;
    nativeForEach = Array.prototype.forEach;
    nativeMap = Array.prototype.map;
    this.each = function (obj, iterator, context) {
      if (obj === null) {
        return
      }
      if (nativeForEach && obj.forEach === nativeForEach) {
        obj.forEach(iterator, context);
      } else if (obj.length === +obj.length) {
        for (var i = 0, l = obj.length; i < l; i++) {
          if (iterator.call(context, obj[i], i, obj) === {}) return
        }
      } else {
        for (var key in obj) {
          if (obj.hasOwnProperty(key)) {
            if (iterator.call(context, obj[key], key, obj) === {}) return
          }
        }
      }
    };
    this.map = function (obj, iterator, context) {
      var results = [];
      if (obj == null) return results
      if (nativeMap && obj.map === nativeMap) return obj.map(iterator, context)
      this.each(obj, function (value, index, list) {
        results[results.length] = iterator.call(context, value, index, list);
      });
      return results
    };
    if (typeof options == 'object') {
      this.hasher = options.hasher;
      this.screen_resolution = options.screen_resolution;
      this.screen_orientation = options.screen_orientation;
      this.canvas = options.canvas;
      this.ie_activex = options.ie_activex;
    } else if (typeof options == 'function') {
      this.hasher = options;
    }
  };
  Fingerprint.prototype = {
    get: function () {
      var keys = [];
      keys.push(navigator.userAgent);
      keys.push(navigator.language);
      keys.push(screen.colorDepth);
      if (this.screen_resolution) {
        var resolution = this.getScreenResolution();
        if (typeof resolution !== 'undefined') {
          keys.push(this.getScreenResolution().join('x'));
        }
      }
      keys.push(new Date().getTimezoneOffset());
      keys.push(this.hasSessionStorage());
      keys.push(this.hasLocalStorage());
      keys.push(!!window.indexedDB);
      if (document.body) {
        keys.push(typeof document.body.addBehavior);
      } else {
        keys.push(typeof undefined);
      }
      keys.push(typeof window.openDatabase);
      keys.push(navigator.cpuClass);
      keys.push(navigator.platform);
      keys.push(navigator.doNotTrack);
      keys.push(this.getPluginsString());
      if (this.canvas && this.isCanvasSupported()) {
        keys.push(this.getCanvasFingerprint());
      }
      if (this.hasher) {
        return this.hasher(keys.join('###'), 31)
      } else {
        return this.murmurhash3_32_gc(keys.join('###'), 31)
      }
    },
    murmurhash3_32_gc: function (key, seed) {
      var remainder, bytes, h1, h1b, c1, c2, k1, i;
      remainder = key.length & 3;
      bytes = key.length - remainder;
      h1 = seed;
      c1 = 0xcc9e2d51;
      c2 = 0x1b873593;
      i = 0;
      while (i < bytes) {
        k1 =
          (key.charCodeAt(i) & 0xff) |
          ((key.charCodeAt(++i) & 0xff) << 8) |
          ((key.charCodeAt(++i) & 0xff) << 16) |
          ((key.charCodeAt(++i) & 0xff) << 24);
        ++i;
        k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
        k1 = (k1 << 15) | (k1 >>> 17);
        k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
        h1 ^= k1;
        h1 = (h1 << 13) | (h1 >>> 19);
        h1b = ((h1 & 0xffff) * 5 + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff;
        h1 = (h1b & 0xffff) + 0x6b64 + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16);
      }
      k1 = 0;
      switch (remainder) {
        case 3:
          k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16;
          break
        case 2:
          k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8;
          break
        case 1:
          k1 ^= key.charCodeAt(i) & 0xff;
          k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
          k1 = (k1 << 15) | (k1 >>> 17);
          k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
          h1 ^= k1;
      }
      h1 ^= key.length;
      h1 ^= h1 >>> 16;
      h1 = ((h1 & 0xffff) * 0x85ebca6b + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
      h1 ^= h1 >>> 13;
      h1 = ((h1 & 0xffff) * 0xc2b2ae35 + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff;
      h1 ^= h1 >>> 16;
      return h1 >>> 0
    },
    hasLocalStorage: function () {
      try {
        return !!window.localStorage
      } catch (e) {
        return true
      }
    },
    hasSessionStorage: function () {
      try {
        return !!window.sessionStorage
      } catch (e) {
        return true
      }
    },
    isCanvasSupported: function () {
      var elem = document.createElement('canvas');
      return !!(elem.getContext && elem.getContext('2d'))
    },
    isIE: function () {
      if (navigator.appName === 'Microsoft Internet Explorer') {
        return true
      } else if (navigator.appName === 'Netscape' && /Trident/.test(navigator.userAgent)) {
        return true
      }
      return false
    },
    getPluginsString: function () {
      if (this.isIE() && this.ie_activex) {
        return this.getIEPluginsString()
      } else {
        return this.getRegularPluginsString()
      }
    },
    getRegularPluginsString: function () {
      return this.map(
        navigator.plugins,
        function (p) {
          var mimeTypes = this.map(p, function (mt) {
            return [mt.type, mt.suffixes].join('~')
          }).join(',');
          return [p.name, p.description, mimeTypes].join('::')
        },
        this,
      ).join(';')
    },
    getIEPluginsString: function () {
      if (window.ActiveXObject) {
        var names = [
          'ShockwaveFlash.ShockwaveFlash',
          'AcroPDF.PDF',
          'PDF.PdfCtrl',
          'QuickTime.QuickTime',
          'rmocx.RealPlayer G2 Control',
          'rmocx.RealPlayer G2 Control.1',
          'RealPlayer.RealPlayer(tm) ActiveX Control (32-bit)',
          'RealVideo.RealVideo(tm) ActiveX Control (32-bit)',
          'RealPlayer',
          'SWCtl.SWCtl',
          'WMPlayer.OCX',
          'AgControl.AgControl',
          'Skype.Detection',
        ];
        return this.map(names, function (name) {
          try {
            new ActiveXObject(name);
            return name
          } catch (e) {
            return null
          }
        }).join(';')
      } else {
        return ''
      }
    },
    getScreenResolution: function () {
      var resolution;
      if (this.screen_orientation) {
        resolution = screen.height > screen.width ? [screen.height, screen.width] : [screen.width, screen.height];
      } else {
        resolution = [screen.height, screen.width];
      }
      return resolution
    },
    getCanvasFingerprint: function () {
      var canvas = document.createElement('canvas');
      var ctx = canvas.getContext('2d');
      var txt = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+-={}|[]:"<>?;,.';
      ctx.textBaseline = 'top';
      ctx.font = "14px 'Arial'";
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText(txt, 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText(txt, 4, 17);
      return canvas.toDataURL()
    },
  };
  return Fingerprint
});

const fp = new Fingerprint({
  canvas: true,
  ie_activex: true,
  screen_resolution: true,
});

const fingerprint = {
  uid: fp.get(),
};

var shortAbbrevations = {
    userAgent: 'ua',
    appVersion: 'av',
    rtt: 'gr',
    windowSize: 'ws',
    pluginsLength: 'pl',
    pluginsArray: 'pa',
    errorTrace: 'et',
    productSub: 'ps',
    windowExternal: 'we',
    mimeTypesConsistent: 'mm',
    evalLength: 'ev',
    webGL: 'wg',
    webDriver: 'wd',
    languages: 'lg',
    notificationPermissions: 'np',
    documentElementKeys: 'ek',
    functionBind: 'fb',
    process: 'gp',
    distinctiveProps: 'dp',
    batteryInstance: 'bi',
    getSpeechRecognition: 'sr',
    paymentUrl: 'pu',
    previousScreen: 'pn',
    isLocationPermissionGiven: 'ig',
    getabsmall: 'aw',
    serviceWorkerUpdated: 'sw',
    storeId: 'si',
    isAddressOnCart: 'ia',
    isLocalStorage: 'ls',
    // isSessionStorage: 'is',
    timezone: 'tz',
    touchEventFp: 'te',
};

var generateHash = function (salt, hashValue) { return __awaiter(void 0, void 0, void 0, function () {
    var utf8, hashBuffer, hashArray, hashHex;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                utf8 = new TextEncoder().encode(salt + hashValue);
                return [4 /*yield*/, window.crypto.subtle.digest('SHA-512', utf8)];
            case 1:
                hashBuffer = _a.sent();
                hashArray = Array.from(new Uint8Array(hashBuffer));
                hashHex = hashArray.map(function (bytes) { return bytes.toString(16).padStart(2, '0'); }).join('');
                return [2 /*return*/, hashHex];
        }
    });
}); };

/**
 * Class representing a bot detector.
 *
 * @class
 * @implements {BotDetectorInterface}
 */
var BotDetector = /** @class */ (function () {
    function BotDetector() {
        this.components = undefined;
        this.detections = undefined;
        this.abbrevetedHash = undefined;
    }
    BotDetector.prototype.getComponents = function () {
        return this.components;
    };
    BotDetector.prototype.getDetections = function () {
        return this.detections;
    };
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    BotDetector.prototype.getSources = function () {
        return sources;
    };
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    BotDetector.prototype.getDetectors = function () {
        return detectors;
    };
    BotDetector.prototype.getFpId = function () {
        return fingerprint.uid;
    };
    /**
     * @inheritdoc
     */
    BotDetector.prototype.detect = function () {
        if (this.components === undefined) {
            throw new Error("BotDetector.detect can't be called before BotDetector.collect");
        }
        var components = this.components;
        var detectors = this.getDetectors();
        var detections = {};
        var finalDetection = {
            bot: false,
        };
        for (var detectorName in detectors) {
            var detector = detectors[detectorName];
            var detectorRes = detector(components);
            var detection = { bot: false };
            if (typeof detectorRes === 'string') {
                detection = { bot: true, botKind: detectorRes };
            }
            else if (detectorRes) {
                detection = { bot: true, botKind: BotKind.Unknown };
            }
            detections[detectorName] = detection;
            if (detection.bot) {
                finalDetection = detection;
            }
        }
        this.detections = detections;
        return finalDetection;
    };
    /**
     * @inheritdoc
     */
    BotDetector.prototype.collect = function () {
        return __awaiter(this, void 0, void 0, function () {
            var sources, components, abbrevetedHash, sourcesKeys;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sources = this.getSources();
                        components = {};
                        abbrevetedHash = {};
                        sourcesKeys = Object.keys(sources);
                        return [4 /*yield*/, Promise.all(sourcesKeys.map(function (sourceKey) { return __awaiter(_this, void 0, void 0, function () {
                                var res, encryptedKey, result, error_1, detailedErrorMsg, detailedErrorMsg;
                                return __generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0:
                                            res = sources[sourceKey];
                                            encryptedKey = shortAbbrevations[sourceKey];
                                            _a.label = 1;
                                        case 1:
                                            _a.trys.push([1, 3, , 4]);
                                            return [4 /*yield*/, res()];
                                        case 2:
                                            result = _a.sent();
                                            components[sourceKey] = {
                                                value: result,
                                                state: State.Success,
                                            };
                                            abbrevetedHash[encryptedKey] = JSON.stringify(result);
                                            return [3 /*break*/, 4];
                                        case 3:
                                            error_1 = _a.sent();
                                            if (error_1 instanceof BotdError) {
                                                detailedErrorMsg = "".concat(error_1.name, ": ").concat(error_1.message);
                                                components[sourceKey] = {
                                                    state: error_1.state,
                                                    error: detailedErrorMsg,
                                                };
                                                abbrevetedHash[encryptedKey] = JSON.stringify(detailedErrorMsg);
                                            }
                                            else {
                                                detailedErrorMsg = error_1 instanceof Error ? "".concat(error_1.name, ": ").concat(error_1.message) : String(error_1);
                                                components[sourceKey] = {
                                                    state: State.UnexpectedBehaviour,
                                                    error: detailedErrorMsg,
                                                };
                                                abbrevetedHash[encryptedKey] = JSON.stringify(detailedErrorMsg);
                                            }
                                            return [3 /*break*/, 4];
                                        case 4: return [2 /*return*/];
                                    }
                                });
                            }); }))];
                    case 1:
                        _a.sent();
                        abbrevetedHash.uid = this.getFpId();
                        this.components = components;
                        this.abbrevetedHash = abbrevetedHash;
                        return [2 /*return*/, abbrevetedHash];
                }
            });
        });
    };
    BotDetector.prototype.createHash = function (clientMap, response, secretKey) {
        return __awaiter(this, void 0, void 0, function () {
            var serverMap, encryptedCodeValue, encryptedKeys, fpKeysValue, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        serverMap = response[0], encryptedCodeValue = response[1];
                        encryptedKeys = Object.keys(encryptedCodeValue)[0];
                        fpKeysValue = this.getdecryptedKeys({
                            encryptedKeys: encryptedKeys,
                            fpKeysValue: '',
                            serverMap: serverMap,
                            clientMap: clientMap,
                            char: ['$', '_'],
                        });
                        return [4 /*yield*/, generateHash(secretKey, fpKeysValue)];
                    case 1:
                        data = _a.sent();
                        return [2 /*return*/, data];
                }
            });
        });
    };
    BotDetector.prototype.getdecryptedKeys = function (_a) {
        var encryptedKeys = _a.encryptedKeys, fpKeysValue = _a.fpKeysValue, serverMap = _a.serverMap, clientMap = _a.clientMap, char = _a.char;
        var serverIdentifier = char[0], clientIdentifier = char[1];
        var serverIdentifierIndex = encryptedKeys.lastIndexOf(serverIdentifier);
        var clientIdentifierIndex = encryptedKeys.lastIndexOf(clientIdentifier);
        if (serverIdentifierIndex > clientIdentifierIndex) {
            fpKeysValue = serverMap[encryptedKeys.slice(serverIdentifierIndex + 1, encryptedKeys.length)] + fpKeysValue;
            encryptedKeys = encryptedKeys.slice(0, serverIdentifierIndex);
        }
        else {
            fpKeysValue = clientMap[encryptedKeys.slice(clientIdentifierIndex + 1, encryptedKeys.length)] + fpKeysValue;
            encryptedKeys = encryptedKeys.slice(0, clientIdentifierIndex);
        }
        if (encryptedKeys.length > 0) {
            return this.getdecryptedKeys({ encryptedKeys: encryptedKeys, fpKeysValue: fpKeysValue, serverMap: serverMap, clientMap: clientMap, char: char });
        }
        return fpKeysValue;
    };
    return BotDetector;
}());

function load() {
    return __awaiter(this, void 0, void 0, function () {
        var detector;
        return __generator(this, function (_a) {
            detector = new BotDetector();
            return [2 /*return*/, detector];
        });
    });
}
var index = { load: load };

export { BotKind, BotdError, index as default, load, sources };
